import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { alpha, type Theme } from '@mui/material/styles';
import { ThumbsDown, X } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { REASONS, type Reason } from '../api/feedbackApi';

type Props = {
  sending: boolean;
  failed: boolean;
  onSend: (reason: Reason, note: string) => void;
  onClose: () => void;
};

/** What went wrong with an answer, and an optional note for the research team. Sits under the answer's actions. */
export function ReportProblemCard({ sending, failed, onSend, onClose }: Props) {
  const [reason, setReason] = useState<Reason>('wrong_number');
  const [note, setNote] = useState('');
  const id = useId();
  const ref = useRef<HTMLDivElement>(null);
  // It opens under the answer, often below the fold or behind the composer: bring it into view.
  useEffect(() => { ref.current?.scrollIntoView({ block: 'center', behavior: 'smooth' }); }, []);

  return (
    <Box ref={ref} role="region" aria-labelledby={id} sx={cardSx}>
      <Stack direction="row" sx={{ gap: 1.25, alignItems: 'flex-start', p: 2, pb: 1.5 }}>
        <Box sx={badgeSx} aria-hidden><ThumbsDown size={14} /></Box>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography id={id} variant="subtitle2" sx={{ fontWeight: 600 }}>Report a problem with this answer</Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            The research team reviews every flag. It also goes into the accuracy test set.
          </Typography>
        </Box>
        <IconButton size="small" aria-label="Close" onClick={onClose} sx={{ color: 'text.secondary', mt: -0.5, mr: -0.5 }}>
          <X size={16} />
        </IconButton>
      </Stack>

      <Box sx={{ px: 2, pb: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>What went wrong?</Typography>
        <Stack role="radiogroup" aria-label="What went wrong?" direction="row" sx={{ gap: 1, flexWrap: 'wrap', mb: 2 }}>
          {REASONS.map(([value, label]) => (
            <ButtonBase key={value} role="radio" aria-checked={value === reason} onClick={() => { setReason(value); }} sx={chipSx(value === reason)}>
              <Box component="span" sx={dotSx(value === reason)} aria-hidden />
              {label}
            </ButtonBase>
          ))}
        </Stack>
        <Typography component="label" htmlFor={`${id}-note`} variant="body2" sx={{ display: 'block', fontWeight: 500, mb: 1 }}>
          Note for the research team <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>(optional)</Box>
        </Typography>
        <TextField
          id={`${id}-note`}
          fullWidth
          multiline
          minRows={2}
          size="small"
          placeholder="e.g. The 4Q26 figure is 4,250 on p.3"
          value={note}
          onChange={(e) => { setNote(e.target.value); }}
          slotProps={{ htmlInput: { maxLength: 500 } }}
        />
      </Box>

      <Stack direction="row" sx={{ alignItems: 'center', gap: 1, px: 2, py: 1.25, borderTop: 1, borderColor: 'divider', flexWrap: 'wrap' }}>
        <Typography variant="caption" sx={{ color: failed ? 'error.main' : 'text.secondary', flexGrow: 1 }} role={failed ? 'alert' : undefined}>
          {failed ? 'The feedback did not send. Try again.' : 'Sent with this question, the answer and its sources'}
        </Typography>
        <Button size="small" onClick={onClose} sx={{ color: 'text.secondary' }}>Cancel</Button>
        <Button size="small" variant="contained" disabled={sending} onClick={() => { onSend(reason, note); }}>
          {sending ? 'Sending…' : 'Send feedback'}
        </Button>
      </Stack>
    </Box>
  );
}

// Full width and last in the wrapping actions row, so the card sits on its own line under the buttons.
const cardSx = (theme: Theme) => ({
  flexBasis: '100%',
  order: 99,
  mt: 1,
  border: 1,
  borderColor: 'divider',
  borderRadius: 2,
  bgcolor: 'background.paper',
  boxShadow: `0 1px 3px ${alpha(theme.palette.text.primary, 0.06)}`,
});

const badgeSx = (theme: Theme) => ({
  display: 'grid',
  placeItems: 'center',
  flexShrink: 0,
  width: 28,
  height: 28,
  borderRadius: 1.5,
  color: theme.palette.error.main,
  bgcolor: alpha(theme.palette.error.main, 0.1),
});

const chipSx = (picked: boolean) => (theme: Theme) => ({
  gap: 0.75,
  px: 1.25,
  py: 0.75,
  borderRadius: 1.5,
  border: 1,
  fontSize: '0.8125rem',
  borderColor: picked ? theme.palette.error.main : theme.palette.divider,
  color: picked ? theme.palette.error.dark : theme.palette.text.primary,
  bgcolor: picked ? alpha(theme.palette.error.main, 0.08) : 'transparent',
  transition: 'border-color 120ms, background-color 120ms',
  '&:hover': { borderColor: picked ? theme.palette.error.main : theme.palette.text.secondary },
  '&.Mui-focusVisible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
});

const dotSx = (picked: boolean) => (theme: Theme) => ({
  width: 12,
  height: 12,
  borderRadius: '50%',
  border: `1.5px solid ${picked ? theme.palette.error.main : theme.palette.text.secondary}`,
  boxShadow: picked ? `inset 0 0 0 2px ${theme.palette.background.paper}` : 'none',
  bgcolor: picked ? theme.palette.error.main : 'transparent',
});
